import { ResultDashboardErrorTicket } from '@common/api/errors/types';
import { Input } from '@common/components/atoms/Input';
import { SelectDropdown } from '@common/components/atoms/Select/SelectDropdown';
import { Icon } from '@common/components/foundations/icons';
import { Typography } from '@common/components/foundations/Typography';
import { RPGPopover } from '@common/components/molecules/Popover/Popover';
import { Table } from '@common/components/molecules/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@common/components/molecules/Tooltip';
import { useModal } from '@common/hooks/useModal';
import { browserTimezone } from '@common/utils/browserTimezone';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { debounce } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { getBadgeColor, getStatus } from '@pages/Admin/ErrorTickets/common/utils/status';
import { DASHBOARD_TAGS } from '@pages/Admin/DashboardErrorTickets/common/constants/tags-option';
import { DASHBOARD_TICKET_STATUS } from '@pages/Admin/DashboardErrorTickets/common/constants/ticket-status';
import { UpdateMultipleErrorTicketsModal as UpdateMultipleDashboardErrorTicketsModal } from '@pages/Admin/DashboardErrorTickets/components/UpdateMultipleDashboardErrorTicketsModal';
import { useDashboardErrorTickets } from '@pages/Admin/DashboardErrorTickets/hooks/useDashboardErrorTickets';
import { DEVELOPER_OPTIONS } from '@common/constants/developer-options';
import { useTranslation } from 'react-i18next';
import { useUpdateMultipleTicketStore } from '@pages/Admin/ErrorTickets/store/updateMultiple';
import { ActionIcon, Button, Checkbox, Badge } from '@mantine/core';
import { UpdateDashboardErrorTicketModal } from '@pages/Admin/DashboardErrorTickets/components/UpdateDashboardErrorTicketModal';

dayjs.extend(utc);
dayjs.extend(timezone);
const schema = z.object({
  page: z.number(),
  ticket_status: z.number().optional(),
  ticket_type: z.number().optional(),
  ticket_tag: z.number().optional(),
  search: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  developer: z.string().optional(),
  tags: z.string().optional()
});

type Params = z.infer<typeof schema>;

const filterEmptyValues = (params: Params): Params => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([_key, value]) => value !== null && value !== undefined && value !== null
    )
  ) as Params;
};

const defaultValues = {
  page: 1,
  ticket_status: 1,
  ticket_type: 1,
  search: undefined,
  developer: undefined,
  ticket_tag: undefined,
  tags: undefined
};

// Main Function
export const DashboardTicketsPage = () => {
  const { watch, reset, control, getValues } = useForm<z.infer<typeof schema>>({
    defaultValues,
    resolver: zodResolver(schema)
  });
  const [selectionTicket, setSelectionTicket] = useState<ResultDashboardErrorTicket>();
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  const { isOpen: editOpen, openModal: editClick, closeModal: editClose } = useModal();
  const {
    isOpen: editMultipleOpen,
    openModal: editMultipleClick,
    closeModal: editMultipleClose
  } = useModal();

  const debouncedSearch = debounce((value) => onResetAndSet('search', value), 1000);

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const onResetAndSet = useCallback(
    (field: keyof Params, value: Params[typeof field]) => {
      reset(
        filterEmptyValues({
          ...defaultValues,
          [field]: value
        })
      );
    },
    [reset, defaultValues, filterEmptyValues]
  );

  const {
    dashboardErrorTicket,
    query: { isLoading, refetch, isFetching }
  } = useDashboardErrorTickets(
    filterEmptyValues({
      page: watch('page'),
      search: getValues('search'),
      start_date: watch('start_date'),
      end_date: watch('end_date'),
      ticket_status: watch('ticket_status'),
      ticket_type: watch('ticket_type'),
      ticket_tag: watch('ticket_tag'),
      developer: watch('developer'),
      tags: watch('tags')
    })
  );
  const { t } = useTranslation();

  const { IsUpdateMultipleTicketSuccess, setUpdateMultipleTicketSuccess } =
    useUpdateMultipleTicketStore();

  const ROWS_PER_PAGE = 20;

  const startIndex = dashboardErrorTicket?.current
    ? (dashboardErrorTicket.current - 1) * ROWS_PER_PAGE + 1
    : 0;
  const endIndex =
    dashboardErrorTicket?.current && dashboardErrorTicket.results
      ? startIndex + dashboardErrorTicket.results.length - 1
      : 0;

  const handleNext = () => {
    if (!dashboardErrorTicket?.next) return;
    const params = new URLSearchParams(dashboardErrorTicket.next.split('?')[1]);
    const page = params.get('page');
    if (!page) return;

    reset(
      filterEmptyValues({
        ...defaultValues,
        page: parseInt(page)
      })
    );
  };

  const handlePrevious = () => {
    const currentPage = watch('page');
    const previousPage = currentPage - 1;
    if (previousPage <= 0) return;

    reset(
      filterEmptyValues({
        ...defaultValues,
        page: previousPage
      })
    );
  };

  const handleCheckboxChange = (isChecked: boolean, id: string) => {
    // If the checkbox is checked and the ID is not already in the array, add it
    if (isChecked && !selectedTickets.includes(id)) {
      setSelectedTickets((prev) => [...prev, id]);
    }
    // If the checkbox is unchecked and the ID is in the array, remove it
    else if (!isChecked && selectedTickets.includes(id)) {
      setSelectedTickets((prev) => prev.filter((ticketId) => ticketId !== id));
    }
  };

  useEffect(() => {
    if (IsUpdateMultipleTicketSuccess) {
      setTimeout(() => {
        setSelectedTickets([]);
        setUpdateMultipleTicketSuccess(false);
        refetch();
      }, 800);
    }
  }, [IsUpdateMultipleTicketSuccess]);

  const columns = [
    {
      id: 'select',
      header: ({ table }: any) => {
        return (
          <Checkbox
            checked={
              (table?.getIsAllPageRowsSelected() || table?.getIsSomePageRowsSelected()) &&
              selectedTickets?.length > 0
            }
            onChange={(e) => {
              const value = e.target.checked;
              if (value) {
                const allRowIds = table?.options?.data?.map((r: any) => r.id);
                setSelectedTickets(allRowIds);
              } else {
                setSelectedTickets([]);
              }
              table.toggleAllPageRowsSelected(value);
            }}
            indeterminate={table.getIsSomePageRowsSelected() && selectedTickets?.length > 0}
            aria-label="Select all"
          />
        );
      },

      cell: ({ row }: any) => {
        return (
          <div>
            <Checkbox
              checked={row?.getIsSelected() && selectedTickets?.includes(row?.original?.id)}
              onChange={(e) => {
                const value = e.target.checked;
                handleCheckboxChange(value, row?.original?.id);
                row?.toggleSelected(value);
              }}
              onClick={editClose}
              aria-label="Select row"
            />
          </div>
        );
      }
    },
    {
      header: 'ID',
      accessorKey: 'hotel',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Hotel',
      accessorKey: 'hotel_name',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Tag',
      accessorKey: 'tags',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Count',
      accessorKey: 'count',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'PMS/CM',
      accessorKey: 'pms_name',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey" className="capitalize">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Responsible Person',
      accessorKey: 'responsible_person',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Reseller',
      accessorKey: 'reseller',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Developer',
      accessorKey: 'developer',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {row?.getValue() as string}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Date Modified',
      accessorKey: 'modified_date',
      cell: (row: any) => {
        return (
          <div className="flex items-center gap-3">
            <Typography element="span" color="copyTextGrey">
              {dayjs.utc(row?.getValue()).tz(browserTimezone).format('YYYY-MM-DD HH:mm')}
            </Typography>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Status',
      accessorKey: 'ticket_status',
      cell: (row: any) => (
        <Badge color={getBadgeColor(row?.getValue())}>{getStatus(row?.getValue())}</Badge>
      ),
      meta: {
        showOnMobile: true
      }
    },
    {
      header: 'Create Ticket',
      accessorKey: 'actions',
      cell: (row: any) => {
        const data = row?.row?.original;
        const jiraTicketUrl =
          'https://roompricegenie.atlassian.net/secure/CreateIssueDetails!init.jspa?issuetype=10009&pid=10015' +
          '&summary=Hotel+Id%3A+' +
          encodeURIComponent(data?.hotel) +
          '+-+' +
          encodeURIComponent(data?.pms_name) +
          '+-+' +
          '&description=' +
          'Hotel+ID%3A+' +
          encodeURIComponent(data?.hotel) +
          '%0AHotel+Name%3A+' +
          encodeURIComponent(data?.hotel_name) +
          '%0AResponsible+Person%3A+' +
          encodeURIComponent(data?.responsible_person) +
          '%0AReseller%3A+' +
          encodeURIComponent(data?.reseller) +
          '%0APMS%2FCM+Name%3A+' +
          encodeURIComponent(data?.pms_name) +
          `&labels=${(data?.pms_name as string)
            ?.split(' ')
            ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            ?.join('')}`;
        return (
          <div className="flex items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <ActionIcon
                    onClick={() => {
                      window.open(jiraTicketUrl, '_blank');
                      setTimeout(() => {
                        editClose();
                      }, 500);
                    }}
                  >
                    <Icon.Add />
                  </ActionIcon>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-white text-darkGrey">
                  Create Jira Ticket
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      meta: {
        showOnMobile: true
      }
    }
  ];

  const TableHeader = () => {
    return (
      <>
        {selectedTickets.length > 1 ? (
          <div className="flex w-full justify-between py-3">
            <Typography variant="h6" element="h6" className="font-medium" color="darkGrey">
              {selectedTickets.length} tickets selected
            </Typography>
          </div>
        ) : (
          <div className="flex w-full justify-between py-3">
            <Typography variant="h6" element="h6" className="font-medium" color="darkGrey">
              Dashboard Error Tickets
            </Typography>
          </div>
        )}
      </>
    );
  };
  return (
    <>
      <Table
        isHover
        isRowClickable
        columns={columns}
        skeletonCount={20}
        isFetching={isLoading || isFetching}
        data={!isLoading ? (dashboardErrorTicket?.results as any) : []}
        headerComponent={<TableHeader />}
        next={handleNext}
        isNext={!!dashboardErrorTicket?.next}
        previous={handlePrevious}
        isPrevious={watch('page') > 1}
        count={`${startIndex}-${endIndex}`}
        totalCount={dashboardErrorTicket?.count}
        manualPagination={true}
        rowsPerPage={ROWS_PER_PAGE}
        onClickRow={(row) => {
          if (row.column.id === 'select') return;
          editClick();
          setSelectionTicket(row?.row?.original);
        }}
        actionComponents={
          selectedTickets.length > 1 ? (
            <div className="flex items-center justify-center gap-5">
              <Button
                onClick={() => {
                  editMultipleClick();
                }}
              >
                Update
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-5">
              <div>
                <Controller
                  name="search"
                  control={control}
                  render={({ field: { value, name, onChange } }) => (
                    <Input
                      placeholder="Search"
                      background="grey"
                      name={name}
                      id={name}
                      type="text"
                      value={value}
                      onChange={(e) => {
                        handleSearch(e.target.value);
                        onChange(e);
                      }}
                      onClear={() => {
                        reset(defaultValues);
                      }}
                    />
                  )}
                />
              </div>
              <div>
                <RPGPopover>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <RPGPopover.Button as={ActionIcon}>
                          <Icon.FilterList />
                        </RPGPopover.Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="bg-white text-darkGrey">
                        Filter
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <RPGPopover.Panel>
                    <div className="mb-2 flex items-center gap-3">
                      <Typography className="text-meta-1-medium" color="darkGrey">
                        {t('Filters')}
                      </Typography>
                      <Button type="button" size="compact-xs" onClick={() => reset(defaultValues)}>
                        {t('Reset')}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                          Ticket Tag
                        </Typography>
                        <Controller
                          control={control}
                          name="tags"
                          render={({ field: { value, name, onChange } }) => (
                            <SelectDropdown
                              fullWidth
                              placeholder="Select Ticket Tag"
                              name={name}
                              key={name}
                              value={value || ''}
                              options={DASHBOARD_TAGS || []}
                              onChange={(value) => {
                                onChange(value);
                                onResetAndSet('tags', value);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                          Ticket Status
                        </Typography>
                        <Controller
                          control={control}
                          name="ticket_status"
                          render={({ field: { value, name, onChange } }) => (
                            <SelectDropdown
                              fullWidth
                              placeholder="Select Ticket Status"
                              name={name}
                              key={name}
                              value={value || ''}
                              options={DASHBOARD_TICKET_STATUS}
                              onChange={(value) => {
                                onChange(value);
                                onResetAndSet('ticket_status', value);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Typography element="p" variant="meta-1" className="mb-1 text-grey">
                          Developer
                        </Typography>
                        <Controller
                          control={control}
                          name="developer"
                          render={({ field: { value, name, onChange } }) => (
                            <SelectDropdown
                              fullWidth
                              placeholder="Select Developer"
                              name={name}
                              key={name}
                              value={value || ''}
                              options={DEVELOPER_OPTIONS}
                              onChange={(value) => {
                                onChange(value);
                                onResetAndSet('developer', value);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </RPGPopover.Panel>
                </RPGPopover>
              </div>
            </div>
          )
        }
      />
      <UpdateDashboardErrorTicketModal
        data={selectionTicket as ResultDashboardErrorTicket}
        isOpen={editOpen}
        onClose={editClose}
      />
      <UpdateMultipleDashboardErrorTicketsModal
        isOpen={editMultipleOpen}
        onClose={editMultipleClose}
        ticketsIds={selectedTickets}
      />
    </>
  );
};
